import { Component } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { isChunkLoadError, reloadOnceForChunkError } from '@fuse/utils/chunkReload';
import logger from 'app/utils/logger';

/**
 * PGSS-5456: the app had no error boundary anywhere.
 *
 * `createRoot().render(<App />)` with no boundary means React unmounts the ENTIRE tree on any
 * throw during render or in an effect -- #root empties and the user gets a blank/black screen
 * with nothing logged and nothing to report. That is the mechanism behind "blank screen when
 * navigating from Outbound Shipment to Calendar": whichever throw fires, the symptom is the same
 * and it is invisible.
 *
 * Placed around the route content (see FuseSuspense) so a failing page loses only the content
 * area -- the toolbar and navigation survive and the user can navigate away without refreshing.
 *
 * PGSS-5492: production builds keep the short friendly copy only. Exception text and the
 * component stack stay on local `yarn start` (and in the console everywhere).
 */
export function shouldShowErrorDetails(nodeEnv = process.env.NODE_ENV) {
  return nodeEnv !== 'production';
}

export class FuseErrorBoundaryInner extends Component {
  constructor(props) {
    super(props);
    this.state = {
      error: null,
      componentStack: '',
      reloading: false,
      resetKey: props.resetKey,
    };
  }

  static getDerivedStateFromError(error) {
    // Suppress the panel up front for a stale chunk, so it does not flash in the moment before
    // the tab reloads. componentDidCatch corrects this if the reload is declined.
    return { error, reloading: isChunkLoadError(error) };
  }

  /**
   * Clear a caught error when the route changes, so a page that failed does not hold the content
   * area hostage once the user navigates somewhere else.
   */
  static getDerivedStateFromProps(props, state) {
    if (props.resetKey !== state.resetKey) {
      return { error: null, componentStack: '', reloading: false, resetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error, info) {
    const componentStack = info?.componentStack || '';

    // Log unconditionally. The whole reason this bug was unreportable is that nothing reached the
    // console -- QA saw a blank screen and had nothing to attach to the ticket.
    logger.error('[FuseErrorBoundary] uncaught error in route content:', error, componentStack);

    // Only a reload that ACTUALLY started may keep the panel hidden. reloadOnceForChunkError
    // declines inside its cooldown, and believing it anyway would render null forever -- which is
    // the very blank screen this boundary exists to remove.
    const reloading = reloadOnceForChunkError(error);

    this.setState({ componentStack, reloading });
  }

  handleRetry = () => {
    this.setState({ error: null, componentStack: '', reloading: false });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { error, componentStack, reloading } = this.state;
    const showDetails = shouldShowErrorDetails();

    if (!error) {
      return this.props.children;
    }

    // A reload is already in flight; render nothing rather than flash a panel.
    if (reloading) {
      return null;
    }

    return (
      <div className="flex flex-1 items-center justify-center p-24">
        <Paper className="flex flex-col w-full max-w-2xl p-24 rounded-16 shadow">
          <Typography className="text-20 font-semibold mb-8">
            This page could not be displayed
          </Typography>

          <Typography className="text-14 mb-16" color="text.secondary">
            Something went wrong while loading this screen. The rest of the application is still
            usable -- you can retry, reload, or navigate somewhere else.
          </Typography>

          {showDetails && (
            <>
              <Typography
                component="pre"
                className="text-12 whitespace-pre-wrap break-words p-12 mb-16 rounded-8 overflow-auto"
                sx={{ bgcolor: 'action.hover', fontFamily: 'monospace', maxHeight: 256 }}
              >
                {String(error?.message || error)}
                {componentStack ? `\n${componentStack}` : ''}
              </Typography>

              <Typography className="text-12 mb-16" color="text.secondary">
                Please include the text above when reporting this.
              </Typography>
            </>
          )}

          <div className="flex gap-8">
            <Button variant="contained" color="primary" onClick={this.handleRetry}>
              Try again
            </Button>
            <Button variant="outlined" color="primary" onClick={this.handleReload}>
              Reload page
            </Button>
          </div>
        </Paper>
      </div>
    );
  }
}

FuseErrorBoundaryInner.propTypes = {
  // Changing this clears a caught error. FuseErrorBoundary feeds it the current pathname.
  resetKey: PropTypes.string,
};

FuseErrorBoundaryInner.defaultProps = {
  resetKey: '',
};

function FuseErrorBoundary(props) {
  const location = useLocation();

  return <FuseErrorBoundaryInner {...props} resetKey={location.pathname} />;
}

export default FuseErrorBoundary;
